import { useState } from "react";
import { ArrowLeft, ArrowRight, Check, Loader2, Send } from "lucide-react";
import { REGISTERS, FLOAT, money, cashCents, toCents, kioskSubmitClosing } from "../../lib/closing";
import { RegisterCountForm } from "./RegisterCountForm";

const blank = () => ({ counts: {}, checks: "", sale: "" });

const summarize = (regs) => {
  const rows = REGISTERS.map((r) => {
    const cash = cashCents(regs[r].counts);
    return { r, cash, deposit: cash - FLOAT * 100, checks: toCents(regs[r].checks), sale: toCents(regs[r].sale) };
  });
  const sum = (k) => rows.reduce((s, x) => s + x[k], 0);
  return { rows, cash: sum("cash"), deposit: sum("deposit"), checks: sum("checks"), sale: sum("sale") };
};

const ReviewTable = ({ s }) => (
  <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3 sm:p-5 overflow-x-auto" data-testid="closing-review">
    <table className="w-full text-[11px] sm:text-sm font-mono">
      <thead className="text-neutral-400 text-xs uppercase">
        <tr><th className="text-left py-2">POS</th><th className="text-right">Counted</th><th className="text-right">Deposit</th><th className="text-right">Checks</th><th className="text-right">Sale</th></tr>
      </thead>
      <tbody>
        {s.rows.map((x) => (
          <tr key={x.r} className="border-t border-neutral-800 [&>td]:pl-2">
            <td className="py-2 font-bold">{x.r}</td><td className="text-right">{money(x.cash / 100)}</td>
            <td className="text-right">{money(x.deposit / 100)}</td><td className="text-right">{money(x.checks / 100)}</td><td className="text-right">{money(x.sale / 100)}</td>
          </tr>
        ))}
        <tr className="border-t-2 border-neutral-600 font-bold sm:text-base [&>td]:pl-2">
          <td className="py-2">Total</td><td className="text-right">{money(s.cash / 100)}</td>
          <td className={`text-right ${s.deposit < 0 ? "text-red-400" : "text-green-400"}`} data-testid="closing-total-cash-deposit">{money(s.deposit / 100)}</td>
          <td className="text-right" data-testid="closing-total-checks">{money(s.checks / 100)}</td>
          <td className="text-right text-yellow-400" data-testid="closing-total-sale">{money(s.sale / 100)}</td>
        </tr>
      </tbody>
    </table>
    <div className="mt-4 flex justify-between gap-3 text-base sm:text-lg font-bold font-mono"><span>Bank deposit<span className="block text-xs text-neutral-400 font-normal">cash + checks</span></span><span className={s.deposit + s.checks < 0 ? "text-red-400" : ""} data-testid="closing-total-deposit">{money((s.deposit + s.checks) / 100)}</span></div>
  </div>
);

export const ClosingWizard = ({ employee, passcode, onExit }) => {
  const [regs, setRegs] = useState({ A: blank(), B: blank(), C: blank() });
  const [step, setStep] = useState(0);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const reg = REGISTERS[step];
  const s = summarize(regs);

  const warnings = s.rows.flatMap((x) => [
    ...(x.sale === 0 ? [`POS ${x.r} total sale is $0`] : []),
    ...(x.deposit < 0 ? [`POS ${x.r} drawer is short of the $200 float`] : []),
  ]);

  const submit = async () => {
    if (!confirming) { setConfirming(true); return; }
    setBusy(true); setErr("");
    try {
      const registers = REGISTERS.map((r) => ({
        register: r,
        counts: Object.fromEntries(Object.entries(regs[r].counts).map(([k, v]) => [k, parseInt(v, 10) || 0])),
        checks_total: Number(regs[r].checks) || 0,
        total_sale: Number(regs[r].sale) || 0,
      }));
      setDone(await kioskSubmitClosing({ employee_id: employee.id, passcode, registers, notes }));
    } catch (e) { setErr(e?.response?.data?.detail || "Could not submit closing"); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="max-w-md mx-auto text-center py-10" data-testid="closing-confirm">
      <div className="w-24 h-24 rounded-full bg-green-500 text-black flex items-center justify-center mx-auto mb-6"><Check size={48} strokeWidth={3} /></div>
      <div className="text-3xl font-bold mb-2">Closing submitted</div>
      <div className="text-neutral-400 mb-6">Thanks, {employee.name}. Deposit {money(done.totals.deposit_total)} · Total sale {money(done.totals.total_sale)}</div>
      <button onClick={onExit} className="px-6 py-3 bg-yellow-400 text-black font-bold rounded-lg" data-testid="closing-finish-btn">Done</button>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto" data-testid="closing-wizard">
      <button onClick={onExit} className="text-neutral-400 hover:text-white flex items-center gap-2 text-sm mb-4" data-testid="closing-cancel"><ArrowLeft size={14} /> Cancel closing</button>
      <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-yellow-400">Closing · {employee.name}</div>
      <div className="flex gap-2 my-4" data-testid="closing-steps">
        {[...REGISTERS.map((r) => `POS ${r}`), "Review"].map((label, i) => (
          <button key={label} onClick={() => { setStep(i); setConfirming(false); }}
            className={`flex-1 py-2 text-sm font-bold rounded border ${i === step ? "bg-yellow-400 text-black border-yellow-400" : "border-neutral-700 text-neutral-400 hover:bg-neutral-900"}`}
            data-testid={`closing-step-${i}`}>{label}</button>
        ))}
      </div>
      {step < 3 ? (
        <RegisterCountForm reg={reg} data={regs[reg]} onChange={(d) => setRegs({ ...regs, [reg]: d })} />
      ) : (
        <>
          <ReviewTable s={s} />
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for the manager (optional)"
            className="w-full mt-4 p-3 bg-neutral-900 border border-neutral-800 rounded-lg text-sm" rows={3} data-testid="closing-notes" />
        </>
      )}
      {step === 3 && confirming && (
        <div className="mt-4 p-4 rounded-lg border border-yellow-400/60 bg-yellow-400/10 text-sm" data-testid="closing-confirm-box">
          {warnings.length > 0 && (
            <ul className="text-red-300 mb-2 list-disc pl-5" data-testid="closing-warnings">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
          )}
          <div className="text-yellow-200">Only one closing per day. After you submit, only a manager can change it. Tap Confirm to submit.</div>
        </div>
      )}
      {err && <div className="text-red-400 text-sm mt-3" data-testid="closing-error">{err}</div>}
      <div className="flex gap-3 mt-5">
        {step > 0 && (
          <button onClick={() => { setStep(step - 1); setConfirming(false); }} className="flex-1 py-4 border border-neutral-700 rounded-lg font-bold flex items-center justify-center gap-2" data-testid="closing-prev-btn">
            <ArrowLeft size={18} /> Back
          </button>
        )}
        {step < 3 ? (
          <button onClick={() => setStep(step + 1)} className="flex-1 py-4 bg-yellow-400 text-black rounded-lg font-bold flex items-center justify-center gap-2" data-testid="closing-next-btn">
            {step === 2 ? "Review" : `Next: POS ${REGISTERS[step + 1]}`} <ArrowRight size={18} />
          </button>
        ) : (
          <button onClick={submit} disabled={busy} className="flex-1 py-4 bg-green-500 text-black rounded-lg font-bold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="closing-submit-btn">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} {confirming ? "Confirm & Submit" : "Submit Closing"}
          </button>
        )}
      </div>
    </div>
  );
};
